import { useLayoutEffect, useMemo, useRef, useState } from "react";

import { useCurrentStateAndParams } from "@uirouter/react";

import { Tab } from "@similarweb/ui-components";
import { Flex } from "@similarweb/ui-components/foundations";

import { WidgetContainer } from "app/components/React/WidgetContainer/WidgetContainer";
import { translate } from "app/filters/i18nFilter";
import { useModelComparisonAveragesGraphData } from "app/pages/digital-marketing/generative-ai/llm-tracker/reports/overview-report/hooks/useModelComparisonAveragesGraphData";
import { useModelComparisonOvertimeData } from "app/pages/digital-marketing/generative-ai/llm-tracker/reports/overview-report/hooks/useModelComparisonOvertimeData";
import { useCoreCompetitorsToggle } from "app/pages/digital-marketing/generative-ai/llm-tracker/reports/overview-report/shared/useCoreCompetitorsToggle";
import { useGranularitySwitcher } from "app/pages/digital-marketing/generative-ai/llm-tracker/shared/hooks/useGranularitySwitcher";
import {
    filterOpenModelsForUser,
    getBaseWidgetRequestParams,
    getDefaultModel,
    getWidgetNoDataProps,
    sortModels,
} from "app/pages/digital-marketing/generative-ai/llm-tracker/shared/LLMTrackerUtils";
import type {
    ILLMReportWidget,
    ILLMWidgetRequestParams,
} from "app/pages/digital-marketing/generative-ai/llm-tracker/shared/types";
import {
    CampaignStatus,
    LLModel,
    ReportStatus,
} from "app/pages/digital-marketing/generative-ai/llm-tracker/shared/types";
import { allTrackers } from "app/services/track/track";
import { GranularityNames } from "app/UtilitiesAndConstants/Constants/Moment";

import { CompareChartSection } from "./CompareChartSection";
import { ALL_TOPICS_NAME } from "app/pages/digital-marketing/generative-ai/llm-tracker/shared/LLMConsts";
import { MIN_DAYS_FOR_WEEKLY_GRANULARITY, VIEW_MODE_CONFIG } from "./consts";
import { FullWidthTabList, FullWidthTabs, ModelComparisonWrapper } from "./ModelComparisonComponent.style";
import { OvertimeChartSection } from "./OvertimeChartSection";
import { EViewMode } from "./types";

const VIEW_MODE_TAB_ORDER: EViewMode[] = [EViewMode.VisibilityOvertime, EViewMode.Visibility, EViewMode.Sentiment];

export const ModelComparisonComponent = ({
    campaign,
    campaignConfig,
    reportStatus,
    reportDuration,
}: ILLMReportWidget) => {
    const {
        params: { topic: queryTopic },
    } = useCurrentStateAndParams();

    const selectedTopic = (queryTopic as string) ?? ALL_TOPICS_NAME;
    const canLoadData = reportStatus === ReportStatus.Ready && !!campaign;
    const models = useMemo<LLModel[]>(() => {
        const defaultModel = getDefaultModel(campaign);
        return filterOpenModelsForUser(
            sortModels(
                Object.entries(campaign.configs)
                    .filter(([model, config]) => model !== LLModel.All && config.status === CampaignStatus.Completed)
                    .map(([model]) => model as LLModel),
                defaultModel,
            ),
            campaign.isDemo,
        );
    }, [campaign]);

    const apiParams = useMemo<ILLMWidgetRequestParams>(
        () => getBaseWidgetRequestParams(campaign, campaignConfig, reportDuration),
        [campaign, campaignConfig, reportDuration],
    );

    // View mode state
    const [selectedViewMode, setSelectedViewMode] = useState<EViewMode>(EViewMode.VisibilityOvertime);
    const isOvertimeMode = selectedViewMode === EViewMode.VisibilityOvertime;

    // Granularity switcher for line chart
    const hasInitializedGranularity = useRef<boolean>(false);
    const reportDays = reportDuration?.to.endOf("day").diff(reportDuration?.from, "days") ?? 0;
    const granularityItems = [
        { title: "D", value: GranularityNames.DAILY, disabled: false },
        { title: "W", value: GranularityNames.WEEKLY, disabled: reportDays < MIN_DAYS_FOR_WEEKLY_GRANULARITY },
    ];
    const { selectedGranularity, setSelectedGranularity, granularitySwitcherConfig } = useGranularitySwitcher({
        items: granularityItems,
        defaultValue: GranularityNames.DAILY,
        trackingName: "Model Comparison Graph",
    });

    const hasCompetitiveSet = campaign?.competitors?.length > 0;
    const { isCoreCompetitorsOn, onOffSwitchConfig } = useCoreCompetitorsToggle({ hasCompetitiveSet });

    // Fetch and transform compare data (for column charts)
    const {
        transformedData: compareTransformedData,
        isLoading: isLoadingCompare,
        isError: isCompareError,
    } = useModelComparisonAveragesGraphData(apiParams, campaign, models, isCoreCompetitorsOn, {
        enabled: canLoadData,
    });

    // Fetch and transform overtime data (only when overtime view is selected)
    const {
        transformedData: overtimeTransformedData,
        isLoading: isLoadingOvertime,
        isError: isOvertimeError,
    } = useModelComparisonOvertimeData(apiParams, campaign, selectedGranularity, isCoreCompetitorsOn, {
        enabled: canLoadData && isOvertimeMode,
    });

    useLayoutEffect(() => {
        hasInitializedGranularity.current = false;
    }, [campaign?.id, reportDays]);

    useLayoutEffect(() => {
        if (overtimeTransformedData && !hasInitializedGranularity.current) {
            const recommendedGranularity =
                reportDays >= MIN_DAYS_FOR_WEEKLY_GRANULARITY ? GranularityNames.WEEKLY : GranularityNames.DAILY;
            setSelectedGranularity(recommendedGranularity);
            hasInitializedGranularity.current = true;
        }
    }, [overtimeTransformedData, reportDays]);

    const handleViewModeChange = (index: number) => {
        const viewMode = VIEW_MODE_TAB_ORDER[index];
        if (!viewMode || viewMode === selectedViewMode) {
            return;
        }
        allTrackers.dispatchTrackEvent("Tab", "click", `Metric view/${viewMode}`);
        setSelectedViewMode(viewMode);
    };

    const hasData = isOvertimeMode ? !!overtimeTransformedData : !!compareTransformedData;

    // Determine loading/error states
    const isLoading = isOvertimeMode ? isLoadingOvertime : isLoadingCompare;
    const isError = isOvertimeMode ? isOvertimeError : isCompareError;
    const shouldShowLoading = (isLoading || !campaign) && reportStatus !== ReportStatus.Error;

    return (
        <ModelComparisonWrapper $hideGranularitySwitcher={!isOvertimeMode}>
            <WidgetContainer
                {...getWidgetNoDataProps(!hasData, isError)}
                title={translate("llm.llm-comparison.title")}
                tooltip={translate("llm.llm-comparison.tooltip")}
                isLoading={shouldShowLoading}
                loaderWidth={"100%"}
                loaderHeight={300}
                headerHeight={80}
                withBorder={false}
                dynamicSwitcherConfig={granularitySwitcherConfig}
                onOffSwitchConfig={onOffSwitchConfig}
            >
                <Flex
                    width="100%"
                    margin="0 0 16px 0"
                    data-automation="model-comparison-view-mode-tabs"
                >
                    <FullWidthTabs
                        selectedIndex={VIEW_MODE_TAB_ORDER.indexOf(selectedViewMode)}
                        onSelect={handleViewModeChange}
                    >
                        <FullWidthTabList>
                            {VIEW_MODE_TAB_ORDER.map((viewMode) => (
                                <Tab
                                    key={viewMode}
                                    data-automation={`model-comparison-view-mode-tab-${viewMode}`}
                                >
                                    {translate(VIEW_MODE_CONFIG[viewMode].displayNameKey)}
                                </Tab>
                            ))}
                        </FullWidthTabList>
                    </FullWidthTabs>
                </Flex>
                {isOvertimeMode ? (
                    <OvertimeChartSection
                        campaign={campaign}
                        transformedData={overtimeTransformedData}
                        selectedTopic={selectedTopic}
                        selectedGranularity={selectedGranularity}
                        reportDuration={reportDuration}
                    />
                ) : (
                    <CompareChartSection
                        transformedData={compareTransformedData}
                        selectedTopic={selectedTopic}
                        models={models}
                        selectedViewMode={selectedViewMode}
                    />
                )}
            </WidgetContainer>
        </ModelComparisonWrapper>
    );
};
